import type { Amount } from '@getstep/proto/common'
import { amountUSD } from '@getstep/sdk'
import { ErrorMessage } from '@hookform/error-message'
import {
    CategoryScale,
    Chart as ChartJS,
    LinearScale,
    LineElement,
    PointElement,
} from 'chart.js'
import get from 'lodash/get'
import type { FunctionComponent } from 'react'
import { useState } from 'react'
import { Line } from 'react-chartjs-2'
import { useForm, useFormState } from 'react-hook-form'

import { Button } from '../button/button'
import { CurrencyInput } from '../input/currency-input'
import { Select } from '../input/select'
import { TextInput } from '../input/text-input'
import { Markdown } from '../markdown'
import { Text } from '../text'
import styles from './savings-calculator.module.scss'
import { calculateSavingsInterestWithMonthlyBalances } from './util'
import { ActionButton } from '@contentful-components/action-button'
import type { StepActionFragment } from '@graphql/fragments/StepsFragment.graphql'
import { TEXT } from '@lib/utils/text'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement)

const SavingsCalculator: FunctionComponent<Props> = ({
    savingsRate = 4.0,
    successMessage,
    action,
}) => {
    const [result, setResult] = useState<{
        labels: string[]
        datasets: {
            label: string
            data: number[]
            borderColor: string
            fill: boolean
        }[]
    } | null>(null)

    const [totals, setTotals] = useState<{
        totalAmount: Amount
        totalContributions: Amount
        totalInterestEarned: Amount
    }>({
        totalAmount: amountUSD(0),
        totalContributions: amountUSD(0),
        totalInterestEarned: amountUSD(0),
    })

    const { handleSubmit, register, control, getValues } = useForm({
        defaultValues: {
            principal: 0,
            interestRate: `${savingsRate}`,
            months: '12',
            monthlyContribution: 0,
        },
    })

    const { isSubmitting, errors, submitCount } = useFormState({ control })

    const handleFormSubmit = async () => {
        const values = getValues()

        const months = parseInt(values.months, 10)
        const principal = amountUSD(values.principal, { precision: 0 })
        const interestRate = parseFloat(values.interestRate)
        const monthlyContributions = amountUSD(values.monthlyContribution, {
            precision: 0,
        })

        const result = calculateSavingsInterestWithMonthlyBalances(
            interestRate,
            parseFloat(principal.value),
            parseFloat(monthlyContributions.value),
            months,
        )

        const chartData = {
            labels: Array.from({ length: months }, (_, i) =>
                i + 1 === 1 ? '1 month' : `${i + 1} months`,
            ),
            datasets: [
                {
                    label: '',
                    data: result.monthlyBalances,
                    borderColor: '#3cd0f5',
                    fill: false,
                },
            ],
        }

        setResult(chartData)
        setTotals({
            totalAmount: amountUSD(result.totalBalance),
            totalContributions: amountUSD(result.totalContributed),
            totalInterestEarned: amountUSD(result.totalInterest),
        })
    }

    return (
        <div className={styles.wrapper}>
            <form
                className={styles.form}
                onSubmit={handleSubmit(handleFormSubmit)}
            >
                <div className={styles['input-group']}>
                    <CurrencyInput
                        format='$#######'
                        label={TEXT.savingsCalculator.principal.label}
                        placeholder='$0'
                        preset='material'
                        size='medium'
                        value={getValues('principal').toString()}
                        valid={!get(errors, 'principal')}
                        validationCount={submitCount}
                        {...register('principal', {
                            required:
                                TEXT.savingsCalculator.principal.requiredError,
                        })}
                        error={
                            <ErrorMessage errors={errors} name='principal' />
                        }
                        fullWidth
                    />
                </div>
                <div className={styles['input-group']}>
                    <CurrencyInput
                        format='$#######'
                        label={TEXT.savingsCalculator.monthlyContribution.label}
                        placeholder='$0'
                        preset='material'
                        size='medium'
                        value={getValues('monthlyContribution').toString()}
                        valid={!get(errors, 'monthlyContribution')}
                        validationCount={submitCount}
                        {...register('monthlyContribution', {
                            required:
                                TEXT.savingsCalculator.monthlyContribution
                                    .requiredError,
                        })}
                        error={
                            <ErrorMessage
                                errors={errors}
                                name='monthlyContribution'
                            />
                        }
                        fullWidth
                    />
                </div>
                <div className={styles['input-group']}>
                    <Select
                        label={TEXT.savingsCalculator.months.label}
                        placeholder='Select'
                        preset='material'
                        size='medium'
                        valid={!get(errors, 'months')}
                        validationCount={submitCount}
                        {...register('months', {
                            required:
                                TEXT.savingsCalculator.months.requiredError,
                        })}
                        error={<ErrorMessage errors={errors} name='months' />}
                        options={[
                            {
                                label: '3 Months',
                                value: '3',
                            },
                            {
                                label: '6 Months',
                                value: '6',
                            },
                            {
                                label: '1 Year',
                                value: '12',
                            },
                            {
                                label: '3 Years',
                                value: '36',
                            },
                            {
                                label: '5 Years',
                                value: '60',
                            },
                            {
                                label: '10 Years',
                                value: '120',
                            },
                            {
                                label: '25 Years',
                                value: '300',
                            },
                            {
                                label: '50 Years',
                                value: '600',
                            },
                        ]}
                        fullWidth
                    />
                </div>
                <div className={styles['input-group']}>
                    <TextInput
                        type='text'
                        label={TEXT.savingsCalculator.interestRate.label}
                        placeholder='0.00'
                        inputMode='numeric'
                        preset='material'
                        size='medium'
                        valid={!get(errors, 'interestRate')}
                        validationCount={submitCount}
                        disabled={isSubmitting}
                        error={
                            <ErrorMessage errors={errors} name='interestRate' />
                        }
                        {...register('interestRate', {
                            required:
                                TEXT.savingsCalculator.interestRate
                                    .requiredError,
                        })}
                        fullWidth
                    />
                </div>

                <div className={styles['button-wrapper']}>
                    <Button
                        size='medium'
                        preset='white'
                        type='submit'
                        animatePress={false}
                        disabled={isSubmitting}
                        className={styles.submit}
                    >
                        {TEXT.savingsCalculator.submit}
                    </Button>
                </div>
            </form>

            {result && (
                <div className={styles.results}>
                    <div className={styles['chart-wrapper']}>
                        <Line data={result} />
                    </div>
                    <div className={styles.totals}>
                        <Text
                            tag='div'
                            typeStyle='body-lg'
                            className={styles['total-row']}
                        >
                            <div>
                                {
                                    TEXT.savingsCalculator.results
                                        .amountYouContribute
                                }
                            </div>
                            <div>${totals.totalContributions.value}</div>
                        </Text>

                        <Text
                            tag='div'
                            typeStyle='body-lg'
                            className={styles['total-row']}
                        >
                            <div>
                                {TEXT.savingsCalculator.results.amountYouEarn}
                            </div>
                            <div>${totals.totalInterestEarned.value}</div>
                        </Text>

                        <hr />
                        <Text
                            tag='div'
                            typeStyle='body-lg'
                            className={styles['total-row']}
                        >
                            <div>
                                {
                                    TEXT.savingsCalculator.results
                                        .totalEstimatedSavings
                                }
                                *
                            </div>
                            <div>${totals.totalAmount.value}</div>
                        </Text>
                        <div>*{TEXT.savingsCalculator.disclaimer}</div>
                    </div>
                    {successMessage && (
                        <div className={styles['success-wrapper']}>
                            <Text tag='div' typeStyle='body-xl' bold>
                                {TEXT.savingsCalculator.cta.heading}
                            </Text>
                            <Markdown
                                markdown={successMessage}
                                typeStyle='body-lg'
                                tag='p'
                            />
                            {action && <ActionButton {...action} />}
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

export default SavingsCalculator

interface Props {
    savingsRate?: number
    successMessage?: string
    action?: StepActionFragment
}
